import axios from 'axios';
import BaseService from './baseService';
import { AuthState } from '@okta/okta-auth-js';

export default class AIService extends BaseService {

    private readonly CHAT_UI_HISTORY_KEY_PREFIX = 'chatUIHistory_';
    private readonly CHAT_HISTORY_FOR_AI_KEY_PREFIX = 'chatHistoryForAI_';

    constructor() {
        super();
    }

    public async chatWithAi(chatId: string, message: string, authState: AuthState | null) {
        if (!chatId) {
            throw new Error("Chat ID is required to send a message to AI.");
        }

        try {
            interface AiResponse {
                response: {
                    llmResponse: {
                        msg: string;
                        chatHistory: any[];
                    };
                    searchLogs: string | null;
                };
            }

            const response = await axios.post<AiResponse>(`${this.REACT_APP_API_BASE_URL}/ask_ai`, {
                chatHistory: this.getChatHistoryForAI(chatId), // This already contains clean AI history
                message: message,
                sessionId: chatId // Pass sessionId to backend
            }, this.getHeaders(authState));

            if (response.status !== 200) {
                throw new Error(`Error: ${response.statusText}`);
            }
            if (!response.data) {
                throw new Error('No response data from AI');
            }

            if (response.data.response.llmResponse.chatHistory) {
                await this.saveChatHistoryForAI(chatId, response.data.response.llmResponse.chatHistory, authState);
            }
            

            return {
                message: response.data.response.llmResponse.msg,
                searchLogs: response.data.response.searchLogs?.length ? response.data.response.searchLogs : 'Message sent to AI, waiting for response...',
            }
        } catch (error) {
            console.error('Error sending message to AI:', error);
            throw error;
        }
    }

    private transformHistoryForUi(history: any[]): any[] {
        return history.map(item => {
            const role = item.role === 'user' ? 'You: ' : 'AI: ';
            // Safely extract text with fallback handling
            let text = '';
            if (item.parts && Array.isArray(item.parts) && item.parts.length > 0) {
                text = item.parts[0].text || '';
            } else if (item.content) {
                // Handle alternative content structure
                text = item.content;
            } else if (item.message) {
                // Handle direct message property
                text = item.message;
            }
            
            // Ensure we never have undefined in the message
            text = text || 'No content available';
            
            return {
                message: `${role}${text}`,
                role: item.role === 'user' ? 'user' : 'model', // Normalize role values
                feedback: null // Initialize with no feedback
            };
        });
    }

    /**
     * Saves the chat history for a specific session to localStorage.
     * @param chatId - A unique identifier for the chat session.
     * @param history - The array of chat messages to save.
     */
    public saveChatUiHistory(chatId: string, history: any[]): void {
        if (!chatId) {
            console.error("Session ID is required to save chat history.");
            return;
        }

        try {
            const key = `${this.CHAT_UI_HISTORY_KEY_PREFIX}${chatId}`;
            const serializedHistory = JSON.stringify(history);
            localStorage.setItem(key, serializedHistory);
            console.log(`Chat history for session ${chatId} saved successfully.`);
        } catch (error) {
            console.error("Failed to save chat history to localStorage:", error);
        }
    }

    /**
     * Saves the chat history for AI processing to localStorage.
     * If the history reaches 20 messages, it will summarize the history first.
     * @param chatId - A unique identifier for the chat session.
     * @param history - The array of chat messages to save.
     * @param authState - The authentication state, used for API calls.
     */
    private async saveChatHistoryForAI(chatId: string, history: any[], authState: AuthState | null): Promise<void> {
        if (!chatId) {
            console.error("Session ID is required to save chat history.");
            return;
        }

        let serializedHistory = "";
        if (history.length >= 20) {
            const summarizedHistory = await this.askAiToSummarizeHistory(history, chatId, authState);
            serializedHistory = JSON.stringify(summarizedHistory);
        }
        else {
            serializedHistory = JSON.stringify(history);
        }

        try {
            const key = `${this.CHAT_HISTORY_FOR_AI_KEY_PREFIX}${chatId}`;
            localStorage.setItem(key, serializedHistory);
            console.log(`Chat history for session ${chatId} saved successfully.`);
        } catch (error) {
            console.error("Failed to save chat history to localStorage:", error);
        }
    }

    /**
     * Sends the chat history to the AI service for summarization.
     * @param history - The chat history to summarize.
     * @param authState - The authentication state, used for API calls.
     * @returns The summarized chat history.
     */
    private async askAiToSummarizeHistory(history: any[], sessionId: string, authState: AuthState | null): Promise<any[]> {
        try {
            interface AiResponse {
                response: {
                    chatHistory: any[];
                };
            }

            const response = await axios.post<AiResponse>(`${this.REACT_APP_API_BASE_URL}/summarize_chat_history`, {
                chatHistory: history,
                sessionId: sessionId
            }, this.getHeaders(authState));

            if (response.status !== 200) {
                throw new Error(`Error: ${response.statusText}`);
            }
            if (!response.data) {
                throw new Error('No response data from AI');
            }

            console.log("Summarized chat history:", response.data);

            return response.data.response.chatHistory;
        } catch (error) {
            console.error('Error summarizing message to AI:', error);
            throw error;
        }
    }

    /**
     * Retrieves the chat history for a specific session from localStorage.
     * @param chatId - The unique identifier for the chat session.
     * @returns The chat history array, or an empty array if not found or on error.
     */
    public getChatUiHistory(chatId: string): any[] {
        if (!chatId) {
            return [];
        }
        try {
            const key = `${this.CHAT_UI_HISTORY_KEY_PREFIX}${chatId}`;
            const serializedHistory = localStorage.getItem(key);
            if (serializedHistory === null) {
                return [];
            }
            const history = JSON.parse(serializedHistory);
            
            // Ensure all items are in object format
            return history.map((item: any) => {
                if (typeof item === 'string') {
                    // Convert string to object format if needed
                    const role = item.startsWith('AI:') ? 'model' : 'user';
                    return {
                        message: item,
                        role: role,
                        feedback: null
                    };
                }
                // Ensure existing objects have role property if missing
                if (typeof item === 'object' && item !== null && !item.role) {
                    const role = item.message && item.message.startsWith('AI:') ? 'model' : 'user';
                    item.role = role;
                }
                return item;
            });
        } catch (error) {
            console.error("Failed to retrieve chat history from localStorage:", error);
            return [];
        }
    }

    public async getAiThinkingLogs(sessionId: string, authState: AuthState | null): Promise<string[]> {
        try {
            const response = await axios.get<{ logs: string[] }>(
                `${this.REACT_APP_API_BASE_URL}/ai/thinking_logs?sessionId=${encodeURIComponent(sessionId)}`,
                this.getHeaders(authState)
            );
            
            if (response.status !== 200) {
                throw new Error(`Error: ${response.statusText}`);
            }
            
            return response.data.logs;
        } catch (error) {
            console.error('Error retrieving log search status from AI:', error);
            return []; // Return empty array on error instead of throwing
        }
    }

    /**
     * Retrieves the chat history for AI processing for a specific session from localStorage.
     * @param chatId - The unique identifier for the chat session.
     * @returns The chat history array, or an empty array if not found or on error.
     */
    public getChatHistoryForAI(chatId: string): any[] {
        if (!chatId) {
            return [];
        }
        try {
            const key = `${this.CHAT_HISTORY_FOR_AI_KEY_PREFIX}${chatId}`;
            const serializedHistory = localStorage.getItem(key);
            if (serializedHistory === null) {
                return [];
            }
            return JSON.parse(serializedHistory);
        } catch (error) {
            console.error("Failed to retrieve chat history from localStorage:", error);
            return [];
        }
    }

    /**
     * Clears the chat history for a specific session from localStorage.
     * @param chatId - The unique identifier for the chat session.
     */
    public clearChatHistory(chatId: string): void {
        if (!chatId) {
            return;
        }
        const key = `${this.CHAT_UI_HISTORY_KEY_PREFIX}${chatId}`;
        localStorage.removeItem(key);
    }

    // Add this method to your AIService class
    public getAllChatIds(): string[] {
        const chatIds: string[] = [];
        const prefix = 'chatUIHistory_'; // Make sure this matches your storage key prefix
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                chatIds.push(key.replace(prefix, ''));
            }
        }
        // Sort by date, assuming the chat ID contains a timestamp
        return chatIds.sort((a, b) => (b.split('-')[1] || 0) > (a.split('-')[1] || 0) ? 1 : -1);
    }

    /**
     * Send feedback for an AI response
     * @param prompt - The user's original question
     * @param response - The AI's response
     * @param feedback - 'positive' or 'negative'
     * @param authState - Authentication state
     */
    public async sendFeedback(prompt: string, response: string, feedback: 'positive' | 'negative', authState: AuthState | null): Promise<void> {
        try {
            const feedbackData = {
                prompt,
                response,
                feedback,
                timestamp: new Date().toISOString()
            };

            const result = await axios.put(`${this.REACT_APP_API_BASE_URL}/feedback`, feedbackData, this.getHeaders(authState));
            
            if (result.status !== 200) {
                throw new Error(`Error: ${result.statusText}`);
            }
            
        } catch (error) {
            console.error('Error sending feedback:', error);
            throw error;
        }
    }

    /**
     * Updates chat UI history with feedback information
     * @param chatId - The unique identifier for the chat session
     * @param chatIndex - Index of the message to update
     * @param feedbackValue - 'positive' or 'negative', null to remove
     */
    public updateChatHistoryWithFeedback(chatId: string, chatIndex: number, feedbackValue: 'positive' | 'negative' | null): void {
        if (!chatId) {
            console.error("Session ID is required to update chat history.");
            return;
        }

        try {
            const history = this.getChatUiHistory(chatId);
            console.log('Updating index:', chatIndex, 'with feedback:', feedbackValue);
            
            if (chatIndex >= 0 && chatIndex < history.length) {
                const item = history[chatIndex];
                
                // Update feedback in object format
                if (typeof item === 'object' && item !== null) {
                    item.feedback = feedbackValue;
                    
                    // Save updated history
                    this.saveChatUiHistory(chatId, history);
                } else {
                    console.error('Item is not an object:', typeof item, item);
                }
            } else {
                console.error('Invalid chat index:', chatIndex, 'history length:', history.length);
            }
        } catch (error) {
            console.error("Failed to update chat history with feedback:", error);
        }
    }

    /**
     * Extracts feedback value from a chat history item
     * @param item - The chat history item object
     * @returns The feedback value ('positive', 'negative') or null if no feedback
     */
    public extractFeedbackFromMessage(item: any): 'positive' | 'negative' | null {        
        if (typeof item === 'object' && item !== null && 'feedback' in item) {
            return item.feedback;
        }
        
        return null;
    }

    /**
     * Gets the clean message text for display
     * @param item - The chat history item object
     * @returns Clean message text for display
     */
    public cleanMessageForDisplay(item: any): string {
        if (typeof item === 'object' && item !== null && 'message' in item) {
            return item.message;
        }
        
        return String(item);
    }
}